import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import regionsTranslations from '../../data/regions.json';
import { addRegionTranslation, getRegionTranslation, reloadTranslations, validateTranslationsFile, saveTranslationsToFile } from '../../services/regionsTranslationsService';
import { getCountryTranslation } from '../../services/countriesTranslationsService';
import { 
  MapIcon, 
  SearchIcon, 
  XCircleIcon,
  PlusIcon,
  EyeIcon,
  EditIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GlobeIcon
} from 'lucide-react';

const Regions = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  
  // State management
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const LIMIT_OPTIONS = [10, 20, 50, 100];
  const [countryId, setCountryId] = useState('');
  
  // Add region modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newRegion, setNewRegion] = useState({
    countryId: '',
    code: '',
    translations: {
      fr: '',
      en: '',
      ar: ''
    }
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch countries for filter
  const fetchCountries = async () => {
    try {
      setCountriesLoading(true);
      console.log('Fetching countries...');
      
      // Try with smaller limit first
      const response = await api.get('/organization/countries?page=1&limit=50');
      console.log('Countries API response:', response.data);
      
      if (response.data.success) {
        console.log('Countries data:', response.data.data.countries);
        setCountries(response.data.data.countries);
        
        // If we got less than total, try to get more
        const total = response.data.data.pagination?.total || 0;
        if (total > 50) {
          console.log(`Total countries: ${total}, fetching more...`);
          try {
            const moreResponse = await api.get(`/organization/countries?page=1&limit=${Math.min(total, 200)}`);
            if (moreResponse.data.success) {
              setCountries(moreResponse.data.data.countries);
              console.log('Updated countries data:', moreResponse.data.data.countries);
            }
          } catch (moreError) {
            console.warn('Could not fetch all countries, using partial list:', moreError);
          }
        }
      } else {
        console.error('API returned success: false', response.data);
        setCountries([]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      console.error('Error response:', error.response?.data);
      
      // Check if it's a validation error
      if (error.response?.data?.message === 'Validation error') {
        console.log('Validation error detected, trying different approach...');
        
        // Try without limit parameter
        try {
          const noLimitResponse = await api.get('/organization/countries?page=1');
          if (noLimitResponse.data.success) {
            setCountries(noLimitResponse.data.data.countries);
            console.log('No limit approach successful:', noLimitResponse.data.data.countries);
            return;
          }
        } catch (noLimitError) {
          console.error('No limit approach also failed:', noLimitError);
        }
      }
      
      // Try with even smaller limit as fallback
      try {
        console.log('Trying with smaller limit...');
        const fallbackResponse = await api.get('/organization/countries?page=1&limit=10');
        if (fallbackResponse.data.success) {
          setCountries(fallbackResponse.data.data.countries);
          console.log('Fallback successful:', fallbackResponse.data.data.countries);
          return;
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
      
      // Final fallback: try to use static data or set empty array
      console.log('All API attempts failed, using empty countries list');
      setCountries([]);
      
      // Show detailed error message to user
      const errorMessage = error.response?.data?.message || error.message || t('regions.countriesFetchError');
      console.error('Final error message:', errorMessage);
      toast.error(`${t('regions.countriesFetchError')}: ${errorMessage}`, { duration: 5000, position: 'top-center' });
    } finally {
      setCountriesLoading(false);
    }
  };

  // Fetch regions - API: /organization/regions or /organization/countries/{id}/regions
  const fetchRegions = async (page = 1, search = '', filterCountryId = countryId, itemsPerPage = limit) => {
    try {
      setLoading(true);
      let url = '/organization/regions';
      if (filterCountryId) {
        url = `/organization/countries/${filterCountryId}/regions`;
      }
      const params = new URLSearchParams({ page: page.toString(), limit: itemsPerPage.toString() });
      if (search.trim()) params.set('search', search.trim());
      const response = await api.get(`${url}?${params.toString()}`);
      
      if (response.data.success) {
        setRegions(response.data.data.regions);
        setTotal(response.data.data.pagination.total);
        setCurrentPage(response.data.data.pagination.page);
      } else {
        throw new Error(response.data.message || 'Failed to fetch regions');
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      toast.error(t('regions.fetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTerm === '') {
      fetchRegions(1, '', countryId);
      return;
    }
    const timeoutId = setTimeout(() => {
      setIsSearching(true);
      fetchRegions(1, searchTerm, countryId).finally(() => setIsSearching(false));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, countryId]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchRegions(1, searchTerm, countryId);
  };

  const clearFilters = () => {
    setCountryId('');
    setSearchTerm('');
    setCurrentPage(1);
    fetchRegions(1, '', '', limit);
  };

  const getActiveFiltersCount = () => (countryId ? 1 : 0);

  const didMountRegions = useRef(false);
  useEffect(() => {
    if (!didMountRegions.current) {
      didMountRegions.current = true;
      return;
    }
    setCurrentPage(1);
    fetchRegions(1, searchTerm, countryId, limit);
  }, [countryId, limit]);

  const translateRegionName = (arabicName, language) => getRegionTranslation(arabicName, language);
  const translateCountryName = (countryName, language) => getCountryTranslation(countryName, language);

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Handle add region
  const handleAddRegion = async (e) => {
    e.preventDefault();
    
    // Validation
    const errors = {};
    if (!newRegion.countryId) {
      errors.countryId = t('regions.countryRequired');
    }
    if (!newRegion.code.trim()) {
      errors.code = t('regions.codeRequired');
    } else if (newRegion.code.length !== 3) {
      errors.code = t('regions.codeLength');
    }
    if (!newRegion.translations.fr.trim()) {
      errors.translation_fr = t('regions.nameRequired_fr');
    }
    if (!newRegion.translations.en.trim()) {
      errors.translation_en = t('regions.nameRequired_en');
    }
    if (!newRegion.translations.ar.trim()) {
      errors.translation_ar = t('regions.nameRequired_ar');
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsAdding(true);
    try {
      const requestData = {
        countryId: newRegion.countryId, // إضافة countryId
        name: newRegion.translations.fr.trim(), // إرسال الاسم الفرنسي إلى قاعدة البيانات
        code: newRegion.code.trim().toUpperCase()
      };
      
      const response = await api.post('/organization/regions', requestData);
      
      if (response.data.success) {
        // Save translations using the new system
        try {
          const translationResult = await addRegionTranslation(
            newRegion.translations.fr.trim(),
            {
              fr: newRegion.translations.fr.trim(),
              en: newRegion.translations.en.trim(),
              ar: newRegion.translations.ar.trim()
            }
          );

          if (translationResult.success) {
            console.log('Translation saved successfully:', translationResult.data);
            
            // Reload translations from file
            const updatedTranslations = reloadTranslations();
            console.log('Updated translations after save:', updatedTranslations);
            
            toast.success(response.data.message || t('regions.addSuccess'), { duration: 3000, position: 'top-center' });
          } else {
            toast.success(response.data.message || t('regions.addSuccess'), { duration: 3000, position: 'top-center' });
            console.warn('Region added but translation save failed:', translationResult.message);
          }
          
          setShowAddModal(false);
          setNewRegion({ countryId: '', code: '', translations: { fr: '', en: '', ar: '' } });
          setFormErrors({});
          fetchRegions(1, searchTerm, countryId);
        } catch (translationError) {
          console.error('Translation save error:', translationError);
          toast.success(response.data.message || t('regions.addSuccess'), { duration: 3000, position: 'top-center' });
          console.warn('Region added but translation save failed');
        }
      } else {
        throw new Error(response.data.message || t('regions.addError'));
      }
    } catch (error) {
      console.error('Error adding region:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = t('regions.addError');
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = Object.values(validationErrors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, { duration: 5000, position: 'top-center' });
    } finally {
      setIsAdding(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setNewRegion(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle translation changes
  const handleTranslationChange = (language, value) => {
    setNewRegion(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [language]: value
      }
    }));
    
    const errorKey = `translation_${language}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  // Open add modal
  const openAddModal = () => {
    setNewRegion({ countryId: '', code: '', translations: { fr: '', en: '', ar: '' } });
    setFormErrors({});
    setShowAddModal(true);
  };

  // Close add modal
  const closeAddModal = () => {
    setShowAddModal(false);
    setNewRegion({ countryId: '', code: '', translations: { fr: '', en: '', ar: '' } });
    setFormErrors({});
  };

  // Handle export translations
  const handleExportTranslations = () => {
    const result = saveTranslationsToFile();
    if (result.success) {
      toast.success(result.message, {
        duration: 5000,
        position: 'top-center'
      });
    } else {
      toast.error(result.message, {
        duration: 3000,
        position: 'top-center'
      });
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (regionId, currentStatus) => {
    try {
      const response = await api.put(`/regions/${regionId}/toggle-status`, {
        is_active: currentStatus === 1 ? 0 : 1
      });
      
      if (response.data.success) {
        toast.success(t('regions.statusUpdated'), { duration: 3000, position: 'top-center' });
        fetchRegions(currentPage, searchTerm, countryId);
      } else {
        throw new Error(response.data.message || t('regions.statusUpdateError'));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(t('regions.statusUpdateError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchRegions(newPage, searchTerm, countryId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <MapIcon className={`h-8 w-8 text-primary-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('regions.title')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('regions.subtitle')}
              </p>
            </div>
          </div>
          <div className={`flex ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <button 
              onClick={openAddModal}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
            >
              <PlusIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('regions.addRegion')}
            </button>
            <button 
              onClick={handleExportTranslations}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <GlobeIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              Export Translations
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters - API: page, limit, search, countryId (path) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <form onSubmit={handleSearch} className="p-4">
          <div className={`flex flex-col lg:flex-row gap-4 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('common.search')}</label>
              <div className="relative">
                <SearchIcon className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                <input type="text" placeholder={t('regions.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors`} />
                {isSearching && <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}><div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" /></div>}
              </div>
            </div>
            <div className="lg:w-40">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('regions.country')}</label>
              <select value={countryId} onChange={(e) => setCountryId(e.target.value)} disabled={countriesLoading} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                <option value="">{countriesLoading ? t('regions.loadingCountries') : t('regions.allCountries')}</option>
                {countries.map((country) => <option key={country.id} value={country.id}>{translateCountryName(country.name, currentLanguage || 'fr')}</option>)}
              </select>
            </div>
            <div className="lg:w-32">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.perPage')}</label>
              <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                {LIMIT_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2 lg:flex-shrink-0">
              <button type="submit" className="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors">{t('common.search')}</button>
              {(countryId || searchTerm) && (
                <button type="button" onClick={clearFilters} title={t('common.clearFilters')} className="p-2.5 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"><XCircleIcon className="h-5 w-5" /></button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Regions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('regions.region')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('regions.code')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('regions.country')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('regions.status')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('regions.createdAt')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('regions.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {regions.map((region) => (
                <tr key={region.id} className="hover:bg-gray-50">
                  <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <MapIcon className="h-4 w-4 text-primary-600" />
                        </div>
                      </div>
                      <div className={`${isRTL ? 'mr-4' : 'ml-4'} ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="text-sm font-medium text-gray-900">
                          {translateRegionName(region.name, currentLanguage || 'fr')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {region.code}
                    </span>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className="text-sm text-gray-900">
                      {translateCountryName(region.country_name, currentLanguage || 'fr')}
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                    <button
                      onClick={() => handleStatusToggle(region.id, region.is_active)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        region.is_active === 1
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      } transition-colors cursor-pointer`}
                    >
                      {region.is_active === 1 ? t('regions.active') : t('regions.inactive')}
                    </button>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {formatDate(region.created_at)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className={`flex items-center ${isRTL ? 'justify-end space-x-reverse space-x-2' : 'justify-start space-x-2'}`}>
                      <button className="text-primary-600 hover:text-primary-900 p-1" title={t('regions.view')}>
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button className="text-blue-600 hover:text-blue-900 p-1" title={t('regions.edit')}>
                        <EditIcon className="h-4 w-4" />
                      </button>
                      <button className="text-red-600 hover:text-red-900 p-1" title={t('regions.delete')}>
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={`bg-white px-4 py-3 flex items-center ${isRTL ? 'justify-between flex-row-reverse' : 'justify-between'} border-t border-gray-200`}>
          <div className={`flex-1 flex ${isRTL ? 'justify-between flex-row-reverse' : 'justify-between'} sm:hidden`}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {t('regions.previous')}
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage * limit >= total}
              className={`${isRTL ? 'mr-3' : 'ml-3'} relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                currentPage * limit >= total ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {t('regions.next')}
            </button>
          </div>
          <div className={`hidden sm:flex-1 sm:flex sm:items-center ${isRTL ? 'sm:justify-between flex-row-reverse' : 'sm:justify-between'}`}>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="text-sm text-gray-700">
                {t('regions.showing')} <span className="font-medium">{((currentPage - 1) * limit) + 1}</span> {t('regions.to')}{' '}
                <span className="font-medium">{Math.min(currentPage * limit, total)}</span>{' '}
                {t('regions.of')} <span className="font-medium">{total}</span> {t('regions.results')}
                {getActiveFiltersCount() > 0 && (
                  <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-primary-600`}>
                    ({getActiveFiltersCount()} {t('regions.filtersApplied')})
                  </span>
                )}
              </p>
            </div>
            <div>
              <nav className={`relative z-0 inline-flex rounded-md shadow-sm ${isRTL ? 'space-x-reverse -space-x-px' : '-space-x-px'}`}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 ${isRTL ? 'rounded-l-md' : 'rounded-r-md'} border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                    currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                
                {Array.from({ length: Math.min(5, Math.ceil(total / limit)) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === page
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage * limit >= total}
                  className={`relative inline-flex items-center px-2 py-2 ${isRTL ? 'rounded-r-md' : 'rounded-l-md'} border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                    currentPage * limit >= total ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Add Region Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : 'flex-row'} mb-4`}>
              <h3 className="text-lg font-medium text-gray-900">
                {t('regions.addRegion')}
              </h3>
              <button
                onClick={closeAddModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">{t('common.close')}</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddRegion} className="space-y-4">
              {/* Country Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('regions.country')} *
                </label>
                <select
                  value={newRegion.countryId}
                  onChange={(e) => handleInputChange('countryId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.countryId ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={countriesLoading}
                >
                  <option value="">
                    {countriesLoading ? t('regions.loadingCountries') : t('regions.selectCountry')}
                  </option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {translateCountryName(country.name, currentLanguage || 'fr')}
                    </option>
                  ))}
                </select>
                {formErrors.countryId && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.countryId}</p>
                )}
              </div>

              {/* Region Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('regions.code')} *
                </label>
                <input
                  type="text"
                  value={newRegion.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.code ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Ex: AUH"
                  maxLength={3}
                />
                {formErrors.code && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.code}</p>
                )}
              </div>

              {/* Translation Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* French Translation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🇫🇷 Français *
                  </label>
                  <input
                    type="text"
                    value={newRegion.translations.fr}
                    onChange={(e) => handleTranslationChange('fr', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.translation_fr ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Émirat d'Abou Dabi"
                  />
                  {formErrors.translation_fr && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.translation_fr}</p>
                  )}
                </div>

                {/* English Translation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🇺🇸 English *
                  </label>
                  <input
                    type="text"
                    value={newRegion.translations.en}
                    onChange={(e) => handleTranslationChange('en', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.translation_en ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Abu Dhabi Emirate"
                  />
                  {formErrors.translation_en && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.translation_en}</p>
                  )}
                </div>

                {/* Arabic Translation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🇸🇦 العربية *
                  </label>
                  <input
                    type="text"
                    value={newRegion.translations.ar}
                    onChange={(e) => handleTranslationChange('ar', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.translation_ar ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="مثال: إمارة أبوظبي"
                  />
                  {formErrors.translation_ar && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.translation_ar}</p>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div className={`flex ${isRTL ? 'flex-row-reverse space-x-reverse space-x-3' : 'flex-row space-x-3'} pt-4`}>
                <button
                  type="button"
                  onClick={closeAddModal}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isAdding}
                  className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isAdding && (
                    <div className={`animate-spin rounded-full h-4 w-4 border-b-2 border-white ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                  )}
                  {isAdding ? t('regions.adding') : t('regions.addRegion')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Regions;
